import { useQuery } from '@tanstack/react-query';
import { StrapiPDPResponse } from '@/types/pdp';

interface UseStrapiPDPReturn {
  pdpData: StrapiPDPResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  extractTextFromStrapiText: (textElements: any[]) => string;
  extractHTMLFromStrapiText: (textElements: any[]) => string;
  getNutritionalInfo: () => any[];
  getIngredients: () => string;
  getSugerenciasDegustacion: () => string;
  getRelatedProducts: () => any[];
  getOtherCustomersTitle: () => string;
  getFAQs: () => any[];
  isOtherCustomersVisible: () => boolean;
  isFAQsVisible: () => boolean;
}

const fetchPDPData = async (productId: string | number): Promise<StrapiPDPResponse> => {
  if (!productId) {
    throw new Error('Product ID is required');
  }

  const response = await fetch(`/api/strapi/pdps?productId=${productId}`);
  
  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }
  
  const responseData = await response.json();
  
  // La API devuelve { success: true, data: { data: [...], meta: {...} } }
  // Necesitamos extraer el data.data para que coincida con nuestra interfaz
  const data: StrapiPDPResponse = {
    data: responseData.data?.data || [],
    meta: responseData.data?.meta || { pagination: { page: 1, pageSize: 25, pageCount: 1, total: 0 } }
  };
  
  return data;
};

export const useStrapiPDP = (productId: string | number): UseStrapiPDPReturn => {
  const {
    data: pdpData,
    isLoading: loading,
    error,
    refetch
  } = useQuery<StrapiPDPResponse, Error>({
    queryKey: ['strapi-pdp', productId],
    queryFn: () => fetchPDPData(productId),
    enabled: !!productId, // Solo ejecutar si productId existe
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Función para extraer texto de los elementos de texto de Strapi
  const extractTextFromStrapiText = (textElements: any[]): string => {
    return textElements
      .map(element => 
        element.children
          ?.map((child: any) => child.text)
          .join('') || ''
      )
      .join(' ');
  };

  // Función para extraer HTML de los elementos de texto de Strapi (para rich text)
  const extractHTMLFromStrapiText = (textElements: any[]): string => {
    if (!textElements || !Array.isArray(textElements)) {
      return '';
    }
    
    return textElements
      .map(element => {
        if (element.children && Array.isArray(element.children)) {
          return element.children
            .map((child: any) => {
              let text = child.text || '';
              if (child.bold) text = `<strong>${text}</strong>`;
              if (child.italic) text = `<em>${text}</em>`;
              return text;
            })
            .join('');
        }
        return '';
      })
      .filter(text => text.trim() !== '') // Filtrar elementos vacíos
      .join('<br/>');
  };

  // Obtener información nutricional
  const getNutritionalInfo = () => {
    if (!pdpData || !pdpData.data.length) {
      return [];
    }
    const nutritionalInfo = pdpData.data[0].Campos_Principales?.Informacion_Nutricional_Lista || [];
    return nutritionalInfo;
  };

  // Obtener ingredientes
  const getIngredients = () => {
    if (!pdpData || !pdpData.data.length) {
      return '';
    }
    const ingredients = pdpData.data[0].Campos_Principales?.Ingredientes || [];
    const ingredientsHTML = extractHTMLFromStrapiText(ingredients);
    return ingredientsHTML;
  };

  // Obtener sugerencias de degustación
  const getSugerenciasDegustacion = () => {
    if (!pdpData || !pdpData.data.length) {
      return '';
    }
    const sugerencias = pdpData.data[0].Sugerencia_degustacion || [];
    const sugerenciasHTML = extractHTMLFromStrapiText(sugerencias);
    return sugerenciasHTML;
  };

  // Obtener productos relacionados
  const getRelatedProducts = () => {
    if (!pdpData || !pdpData.data.length) return [];
    return pdpData.data[0].Otros_Clientes_Tambien_Compraron?.Productos || [];
  };

  // Obtener título de la sección "Otros clientes también compraron"
  const getOtherCustomersTitle = () => {
    if (!pdpData || !pdpData.data.length) return 'Otros clientes también compraron';
    const title = pdpData.data[0].Otros_Clientes_Tambien_Compraron?.Titulo_Seccion || [];
    return extractTextFromStrapiText(title) || 'Otros clientes también compraron';
  };

  // Obtener preguntas frecuentes
  const getFAQs = () => {
    if (!pdpData || !pdpData.data.length) {
      return [];
    }
    const faqs = pdpData.data[0].Preguntas_Frecuentes?.Preguntas_Frecuentes || [];
    return faqs;
  };

  // Verificar si la sección "Otros clientes también compraron" es visible
  const isOtherCustomersVisible = (): boolean => {
    if (!pdpData || !pdpData.data.length) return false;
    return pdpData.data[0].Otros_Clientes_Tambien_Compraron?.Visible ?? false;
  };

  // Verificar si la sección de FAQs es visible
  const isFAQsVisible = (): boolean => {
    if (!pdpData || !pdpData.data.length) return false;
    return pdpData.data[0].Preguntas_Frecuentes?.Visible ?? false;
  };

  return { 
    pdpData: pdpData || null, 
    loading, 
    error: error?.message || null, 
    refetch: () => refetch(),
    extractTextFromStrapiText,
    extractHTMLFromStrapiText,
    getNutritionalInfo,
    getIngredients,
    getSugerenciasDegustacion,
    getRelatedProducts,
    getOtherCustomersTitle,
    getFAQs,
    isOtherCustomersVisible,
    isFAQsVisible
  };
};
